/**
 * PlatformRouter — decides whether to boot the mobile or desktop UI.
 *
 * Three signals, highest-priority first:
 *   1. URL override:         ?mobile=1 | ?mobile=true | ?desktop=1 | ?desktop=true
 *   2. Persisted preference: localStorage['cm_platform'] = 'mobile' | 'desktop'
 *   3. Auto-detection:       isMobile() from ResponsiveHelper
 *
 * The URL override, when set, also updates localStorage so the choice sticks
 * across reloads without keeping the query string.
 */

import { isMobile as autoDetectMobile } from './ResponsiveHelper';

type Platform = 'mobile' | 'desktop';
const STORAGE_KEY = 'cm_platform';

function readUrlOverride(): Platform | null {
  if (typeof window === 'undefined') return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const mobileParam = params.get('mobile');
    const desktopParam = params.get('desktop');
    if (mobileParam === '1' || mobileParam === 'true') return 'mobile';
    if (desktopParam === '1' || desktopParam === 'true') return 'desktop';
    return null;
  } catch {
    return null;
  }
}

function readStoredPreference(): Platform | null {
  if (typeof window === 'undefined') return null;
  try {
    const v = window.localStorage?.getItem(STORAGE_KEY);
    if (v === 'mobile' || v === 'desktop') return v;
    return null;
  } catch {
    return null;
  }
}

function writeStoredPreference(p: Platform): void {
  if (typeof window === 'undefined') return;
  try { window.localStorage?.setItem(STORAGE_KEY, p); } catch { /* ignore */ }
}

/**
 * Returns true if the mobile UI should be used.
 * Evaluated once at call time (no caching) so that URL overrides take effect
 * immediately on load and subsequent calls stay consistent.
 */
export function shouldUseMobileUI(): boolean {
  // 1. URL override — also persists the choice
  const urlOverride = readUrlOverride();
  if (urlOverride) {
    writeStoredPreference(urlOverride);
    return urlOverride === 'mobile';
  }

  // 2. Stored preference from a previous session
  const stored = readStoredPreference();
  if (stored) return stored === 'mobile';

  // 3. Auto-detection (no Phaser scene available at this call site)
  return autoDetectMobile();
}

/**
 * Returns the chosen HUD scene key.
 */
export function getHUDSceneKey(): 'MMOHUDScene' | 'MMOHUDMobileScene' {
  return shouldUseMobileUI() ? 'MMOHUDMobileScene' : 'MMOHUDScene';
}

/**
 * Programmatically set the preference (e.g., from a settings toggle).
 * Returns the new platform so callers can act on it.
 */
export function setPlatformPreference(p: Platform): Platform {
  writeStoredPreference(p);
  return p;
}

/**
 * Clear stored preference and fall back to auto-detection on next load.
 */
export function clearPlatformPreference(): void {
  if (typeof window === 'undefined') return;
  try { window.localStorage?.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}
